import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { CloudApiService } from '@/cloud/services/cloud-api.service';
import { environment } from '../../../environments/environment';
import { FeedbackListQuery, FeedbackPage, FeedbackRoomsSummary, FeedbackScope, FeedbackSummary } from '@/cloud/models/feedback.models';

/** Client for the room feedback endpoints (summary for the Feedback tab, paged entries for the comment list, per-room rollup for the rooms list). */
@Injectable({ providedIn: 'root' })
export class CloudFeedbackService {
    private http = inject(HttpClient);
    private cloudApi = inject(CloudApiService);

    getSummary(scope: FeedbackScope, id: string, from: Date, to: Date, bucketMinutes?: number): Promise<FeedbackSummary> {
        let params = new HttpParams().set('from', from.toISOString()).set('to', to.toISOString());
        if (bucketMinutes) params = params.set('bucketMinutes', bucketMinutes);
        return firstValueFrom(this.http.get<FeedbackSummary>(`${this.base(scope, id)}/summary`, { headers: this.authHeaders(), params }));
    }

    /** Count and average per room of a tenant, lowest average first. */
    getRoomSummaries(namespaceId: string, from: Date, to: Date): Promise<FeedbackRoomsSummary> {
        const params = new HttpParams().set('from', from.toISOString()).set('to', to.toISOString());
        return firstValueFrom(this.http.get<FeedbackRoomsSummary>(`${this.base('namespaces', namespaceId)}/rooms`, { headers: this.authHeaders(), params }));
    }

    list(scope: FeedbackScope, id: string, query: FeedbackListQuery = {}): Promise<FeedbackPage> {
        let params = new HttpParams();
        if (query.from) params = params.set('from', query.from.toISOString());
        if (query.to) params = params.set('to', query.to.toISOString());
        if (query.minRating) params = params.set('minRating', query.minRating);
        if (query.maxRating) params = params.set('maxRating', query.maxRating);
        if (query.commentsOnly) params = params.set('commentsOnly', true);
        if (query.limit) params = params.set('limit', query.limit);
        if (query.cursor) params = params.set('cursor', query.cursor);
        return firstValueFrom(this.http.get<FeedbackPage>(this.base(scope, id), { headers: this.authHeaders(), params }));
    }

    private base(scope: FeedbackScope, id: string): string {
        return `${environment.apiBaseUrl}/${scope}/${encodeURIComponent(id)}/feedback`;
    }

    private authHeaders(): HttpHeaders {
        const token = this.cloudApi.token();
        return token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : new HttpHeaders();
    }
}
